import $ from 'jquery';

export interface SelectableItem {
	id: number;
	name: string;
}

/**
 * Decide which item should be selected after a field is rebuilt.
 */
export function getSelectedItemValue(
	items: SelectableItem[],
	currentValue: string | string[] | number | undefined,
	defaultLabel: string | undefined
): string | null {
	// 1. Restore previous value if still valid.
	if (
		currentValue &&
		typeof currentValue === 'string' &&
		items.some((item) => item.id.toString() === currentValue)
	) {
		return currentValue;
	}

	// 2. Fallback to default label match.
	if (defaultLabel) {
		const match = items.find((item) => item.name === defaultLabel);

		if (match) {
			return match.id.toString();
		}
	}

	// 3. If there is only one valid choice, auto-select it.
	if (items.length === 1) {
		return items[0].id.toString();
	}

	return null;
}

/**
 * Update dropdown options with new items.
 */
export function updateSelectOptions(
	$select: JQuery,
	items: SelectableItem[],
	placeholder: string,
	preserveValue = true
): void {
	const currentValue = preserveValue ? $select.val() : null;
	const defaultLabel = $select.data('gpb-default-value');

	// Clear and rebuild options
	$select.empty();
	$select.append($('<option>', { value: '', text: placeholder }));

	items.forEach((item) => {
		$select.append(
			$('<option>', {
				value: item.id.toString(),
				text: item.name,
			})
		);
	});

	const valueToSet = getSelectedItemValue(
		items,
		currentValue as string | string[] | number | undefined,
		typeof defaultLabel === 'string' ? defaultLabel : undefined
	);

	// Apply value if found
	if (valueToSet) {
		$select.val(valueToSet);
	}

	$select.trigger('change');

	const el = $select.get(0);
	if (el) {
		el.dispatchEvent(new Event('change', { bubbles: true }));
	}
}

/**
 * Update radio button group with new items.
 */
export function updateRadioButtons(
	$radioContainer: JQuery,
	items: SelectableItem[],
	formId: number,
	fieldId: number,
	preserveValue = true
): void {
	const currentValue = preserveValue
		? $radioContainer.find('input[type="radio"]:checked').val()
		: null;

	const defaultLabel = $radioContainer.data('gpb-default-value');

	const valueToSet = getSelectedItemValue(
		items,
		currentValue as string | string[] | number | undefined,
		typeof defaultLabel === 'string' ? defaultLabel : undefined
	);

	// Clear and rebuild radio buttons
	$radioContainer.empty();

	items.forEach((item, index) => {
		const inputId = `choice_${formId}_${fieldId}_${index}`;
		const isChecked = valueToSet && valueToSet === item.id.toString();

		const $li = $('<li>', { class: 'gchoice' });

		const $input = $('<input>', {
			type: 'radio',
			name: `input_${fieldId}`,
			id: inputId,
			value: item.id.toString(),
		});

		if (isChecked) {
			$input.prop('checked', true);
		}

		const $label = $('<label>', {
			for: inputId,
			text: item.name,
		});

		$li.append($input, $label);
		$radioContainer.append($li);
	});

	// Trigger change if a value was selected
	if (valueToSet) {
		const $checked = $radioContainer.find('input[type="radio"]:checked');
		$checked.trigger('change');

		const el = $radioContainer.get(0);
		if (el) {
			el.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}
}
